// Athlete Spotlight: video popup with YouTube sound control.
// The popup opens from a video card; the iframe autoplay-starts muted and the
// sound button toggles the player through the YouTube iframe API postMessage
// protocol (enablejsapi=1). Close: backdrop click, close button, Escape.
// Focus returns to the opener. One controller binds per section root.

(() => {
  'use strict';

  const POPUP_OPEN_MS = 320;
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  const youtubeId = (input) => {
    const m = String(input || '').match(
      /(?:youtu\.be\/|v=|\/shorts\/|\/embed\/)([A-Za-z0-9_-]{11})/
    ) || String(input || '').match(/^([A-Za-z0-9_-]{11})$/);
    return m ? m[1] : '';
  };

  const buildIframe = (video) => {
    const id = youtubeId(video.url);
    if (!id) return null;
    const iframe = document.createElement('iframe');
    iframe.className = 'athlete-spotlight-popup__iframe';
    iframe.title = video.title || 'Video';
    iframe.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    const params = new URLSearchParams({
      autoplay: '1',
      mute: '1',
      playsinline: '1',
      rel: '0',
      enablejsapi: '1',
      origin: window.location.origin,
    });
    iframe.src = 'https://www.youtube-nocookie.com/embed/' + id + '?' + params.toString();
    return iframe;
  };

  const sendToPlayer = (iframe, func, args) => {
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func, args: args || [] }),
      '*'
    );
  };

  // Inline preview: a muted, autoplaying iframe swapped over the poster.
  // Desktop starts on pointerenter and stops on pointerleave; touch devices
  // start on the first tap, and a second tap on the play control opens the
  // popup. Preview iframes carry no sound and no pause control - they self
  // destruct when removed.
  const buildPreviewIframe = (video) => {
    const id = youtubeId(video.url);
    if (!id) return null;
    const iframe = document.createElement('iframe');
    iframe.className = 'athlete-spotlight__preview';
    iframe.tabIndex = -1;
    iframe.setAttribute('aria-hidden', 'true');
    iframe.title = 'Video preview';
    const params = new URLSearchParams({
      autoplay: '1',
      mute: '1',
      loop: '1',
      playlist: id,
      controls: '0',
      modestbranding: '1',
      rel: '0',
      playsinline: '1',
      enablejsapi: '1',
      origin: window.location.origin,
    });
    iframe.src = 'https://www.youtube-nocookie.com/embed/' + id + '?' + params.toString();
    return iframe;
  };

  const IS_TOUCH = window.matchMedia('(hover: none)').matches;

  const bindPreview = (openers, getVideo, openPopup, registerStop) => {
    openers.forEach((btn) => {
      const block = btn.closest('[data-video-block]');
      const card = btn.classList.contains('athlete-spotlight__video-frame')
        ? btn
        : block && block.querySelector('.athlete-spotlight__video-frame');
      if (!card || card.dataset.previewBound) return;
      card.dataset.previewBound = 'true';

      // The preview iframe mounts OUTSIDE the <button> (invalid nesting would
      // leave the preview stuck after the popup closes): the video block
      // wraps the frame button in div.athlete-spotlight__video-box and the
      // iframe overlays it there with pointer-events none.
      const box = btn.closest('.athlete-spotlight__video-box') || card.parentElement;

      let previewIframe = null;
      const startPreview = () => {
        if (previewIframe) return;
        const video = getVideo(btn);
        if (!video) return;
        previewIframe = buildPreviewIframe(video);
        if (!previewIframe) return;
        box.appendChild(previewIframe);
        box.classList.add('is-previewing');
      };
      const stopPreview = () => {
        if (!previewIframe) return;
        box.classList.remove('is-previewing');
        const doomed = previewIframe;
        previewIframe = null;
        window.setTimeout(() => doomed.remove(), 350);
      };
      if (typeof registerStop === 'function') registerStop(btn, stopPreview);

      if (IS_TOUCH) {
        // First tap plays the muted preview; a tap near the play control
        // (generous center zone) opens the popup. A keyboard click
        // (event.detail === 0) opens the popup immediately.
        card.addEventListener('touchstart', startPreview, { passive: true });
        card.addEventListener('click', (event) => {
          if (event.detail === 0) {
            stopPreview();
            openPopup(btn);
            return;
          }
          if (!previewIframe) {
            startPreview();
            return;
          }
          const rect = card.getBoundingClientRect();
          const dx = event.clientX - (rect.left + rect.width / 2);
          const dy = event.clientY - (rect.top + rect.height / 2);
          if (Math.hypot(dx, dy) < Math.max(rect.height * 0.23, 56)) {
            stopPreview();
            openPopup(btn);
          }
        });
      } else {
        card.addEventListener('pointerenter', startPreview);
        card.addEventListener('pointerleave', stopPreview);
        card.addEventListener('click', () => {
          stopPreview();
          openPopup(btn);
        });
      }
    });
  };

  class AthleteSpotlight {
    constructor(sectionRoot) {
      this.section = sectionRoot;
      this.openers = Array.from(
        this.section.querySelectorAll('[data-video-open]')
      );
      this.popup = null;
      this.iframe = null;
      this.lastFocused = null;
      this.isOpen = false;
      this.activeOpener = null;
      this.previewStops = new Map();
      this.bind();
      this.bindScroller();
    }

    videoFor(btn) {
      const block = btn.closest('[data-video-block]');
      if (!block) return null;
      return {
        url: block.dataset.videoUrl || '',
        title: btn.dataset.videoTitle || block.dataset.videoTitle || 'Chase Jackson',
      };
    }

    ensurePopup() {
      if (this.popup) return this.popup;
      const popup = document.createElement('div');
      popup.className = 'athlete-spotlight-popup';
      popup.setAttribute('role', 'dialog');
      popup.setAttribute('aria-modal', 'true');
      popup.hidden = true;
      popup.innerHTML = [
        '<button type="button" class="athlete-spotlight-popup__backdrop" data-backdrop aria-label="Close video" tabindex="-1"></button>',
        '<div class="athlete-spotlight-popup__card">',
        '  <div class="athlete-spotlight-popup__stage" data-stage></div>',
        '  <div class="athlete-spotlight-popup__bar">',
        '    <p class="athlete-spotlight-popup__title" data-title></p>',
        '    <div class="athlete-spotlight-popup__actions">',
        '      <button type="button" class="athlete-spotlight-popup__btn" data-sound data-muted="true" aria-label="Sound toggle" aria-pressed="false">',
        '        <svg class="icon--muted" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M4 9h3l4-3v12l-4-3H4z" fill="currentColor"/><path d="M16 9l5 6M21 9l-5 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
        '        <svg class="icon--sound" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M4 9h3l4-3v12l-4-3H4z" fill="currentColor"/><path d="M16 8.5a5 5 0 010 7M18.5 6a8.5 8.5 0 010 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>',
        '      </button>',
        '      <button type="button" class="athlete-spotlight-popup__btn" data-close aria-label="Close video">',
        '        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
        '      </button>',
        '    </div>',
        '  </div>',
        '</div>',
      ].join('');
      document.body.appendChild(popup);

      popup.querySelector('[data-backdrop]').addEventListener('click', () => this.close());
      popup.querySelector('[data-close]').addEventListener('click', () => this.close());
      popup.querySelector('[data-sound]').addEventListener('click', (e) => {
        const btn = e.currentTarget;
        if (!this.iframe) return;
        const willUnmute = btn.dataset.muted === 'true';
        sendToPlayer(this.iframe, willUnmute ? 'unMute' : 'mute');
        if (willUnmute) sendToPlayer(this.iframe, 'playVideo');
        btn.dataset.muted = willUnmute ? 'false' : 'true';
        // aria-pressed true = sound is on
        btn.setAttribute('aria-pressed', String(willUnmute));
      });

      this.popup = popup;
      this.soundButton = popup.querySelector('[data-sound]');
      this.stage = popup.querySelector('[data-stage]');
      this.titleEl = popup.querySelector('[data-title]');
      return popup;
    }

    open(btn) {
      const video = this.videoFor(btn);
      if (!video || !youtubeId(video.url)) return;
      if (this.isOpen) this.close();
      this.lastFocused = document.activeElement;
      const popup = this.ensurePopup();
      const iframe = buildIframe(video);
      if (!iframe) return;
      this.iframe = iframe;
      this.stage.textContent = '';
      this.stage.appendChild(iframe);
      this.titleEl.textContent = video.title;
      this.soundButton.dataset.muted = 'true';
      this.soundButton.setAttribute('aria-pressed', 'false');
      popup.hidden = false;
      document.body.classList.add('t4-video-popup-open');
      window.requestAnimationFrame(() => popup.classList.add('is-open'));
      this.isOpen = true;
      popup.querySelector('[data-close]').focus({ preventScroll: true });
      if (this.activeOpener) this.activeOpener.setAttribute('aria-expanded', 'false');
      this.activeOpener = btn;
      btn.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      const popup = this.popup;
      popup.classList.remove('is-open');
      document.body.classList.remove('t4-video-popup-open');
      // Stop any inline preview the opener still has running.
      if (this.activeOpener) {
        const stopPreview = this.previewStops.get(this.activeOpener);
        if (typeof stopPreview === 'function') stopPreview();
      }
      const finish = () => {
        if (this.isOpen) return;
        this.stage.textContent = '';
        this.iframe = null;
        popup.hidden = true;
      };
      if (REDUCED_MOTION.matches) finish();
      else window.setTimeout(finish, POPUP_OPEN_MS);
      if (this.activeOpener) {
        this.activeOpener.setAttribute('aria-expanded', 'false');
        this.activeOpener = null;
      }
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus({ preventScroll: true });
      }
    }

    bindScroller() {
      const row = this.section.querySelector('[data-row]');
      if (!row || row.dataset.scrollerBound) return;
      row.dataset.scrollerBound = 'true';
      const prev = this.section.querySelector('[data-row-prev]');
      const next = this.section.querySelector('[data-row-next]');
      if (!prev || !next) return;

      const behavior = REDUCED_MOTION.matches ? 'auto' : 'smooth';
      const step = () => {
        const tile = row.querySelector('li');
        if (!tile) return row.clientWidth;
        const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
        return Math.ceil(tile.getBoundingClientRect().width + gap);
      };
      prev.addEventListener('click', () => {
        row.scrollBy({ left: -step(), behavior });
      });
      next.addEventListener('click', () => {
        row.scrollBy({ left: step(), behavior });
      });

      const update = () => {
        const max = row.scrollWidth - row.clientWidth;
        prev.disabled = row.scrollLeft <= 1;
        next.disabled = row.scrollLeft >= max - 1;
      };
      row.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();
    }

    bind() {
      bindPreview(
        this.openers,
        (btn) => this.videoFor(btn),
        (btn) => this.open(btn),
        (btn, stopPreview) => this.previewStops.set(btn, stopPreview)
      );
      document.addEventListener('keydown', (event) => {
        if (!this.isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusables = Array.from(
          this.popup.querySelectorAll('button, [href], iframe')
        ).filter(
          (el) =>
            !el.hidden &&
            el.getAttribute('tabindex') !== '-1' &&
            el.getClientRects().length > 0
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }
  }

  document
    .querySelectorAll('[data-athlete-spotlight]')
    .forEach((root) => new AthleteSpotlight(root));
})();